import { ModalProps } from '@/types/shared'
import React, { lazy } from 'react'

const modals: { [key: string]: React.ComponentType<ModalProps> } = {}

type LazyContext = {
    keys(): string[]
    (key: string): Promise<{ default: React.ComponentType<ModalProps> }>
}

const register = (context: LazyContext, toName: (key: string) => string) => {
    context.keys().forEach((key: string) => {
        if (!key.startsWith('./') || key === './index.ts') return // skip self and duplicate (absolute) keys
        modals[toName(key).replace(/\.\w+$/, '')] = lazy(() => context(key))
    })
}

// Cross-cutting modals — the ones no single feature owns, opened by key from
// more than one module (or from a shared component). Every file here is a
// modal entry, opened by its path: setModal('Folder/Modal/index'). 'lazy' mode
// gives each its own chunk, downloaded the first time that modal opens.
// A modal owned by one feature does NOT belong here; it belongs in
// src/modules/<Module>/modals, registered by the second scan below.
register(
    // @ts-expect-error - Webpack/Rspack-specific API
    require.context('./', true, /\.(js|jsx|ts|tsx)$/, 'lazy'),
    (key) => key.replace('./', '')
)

// Module-owned modals live in src/modules/<Module>/modals and keep the same
// key without the "<Module>/modals/" prefix, e.g.
// modules/Report/modals/ServiceCostModals/CreateModal/index.tsx is still
// opened with setModal('ServiceCostModals/CreateModal/index').
// The pattern is anchored one level deep, so a modal under
// modules/<Module>/modules/<Sub>/modals is NOT registered: keep module modals
// flat at modules/<Module>/modals.
register(
    // @ts-expect-error - Webpack/Rspack-specific API
    require.context(
        '../../modules',
        true,
        /^\.\/[^/]+\/modals\/.*\.(js|jsx|ts|tsx)$/,
        'lazy'
    ),
    (key) => key.replace(/^\.\/[^/]+\/modals\//, '')
)

/** A package's modals: key -> lazy import, declared in packages/<dir>/src/modals.ts. */
export type PackageModalEntries = Record<
    string,
    () => Promise<{ default: React.ComponentType<ModalProps> }>
>

// Workspace packages (#204). require.context cannot see packages/, so a package
// declares its modals in packages/<dir>/src/modals.ts and its host registers
// them here when the package is created (register-<x>.tsx), before anything
// renders. Keys are unchanged, so setModal('<Group>/<Modal>/index') keeps
// working after the move. A key that is already taken THROWS: two modals
// behind one key would open whichever registered last, silently.
// check:modal-keys reads the same files and reports both cases statically.
export const registerPackageModals = (entries: PackageModalEntries) => {
    for (const [key, load] of Object.entries(entries)) {
        if (key in modals)
            throw new Error(
                `registerPackageModals: modal key "${key}" is already registered`
            )
        modals[key] = lazy(load)
    }
}

export default modals
